import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, MapPin, Heart, Music, Database, Lock, Users, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-6 max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Política de Privacidade</h1>
        </div>
        
        <p className="text-muted-foreground mb-8">
          A You Better Run ("YBR", "nós", "nosso") está comprometida em proteger sua privacidade. 
          Esta política explica como coletamos, usamos e protegemos seus dados pessoais em conformidade 
          com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
        </p>

        <div className="space-y-6">
          {/* Dados Coletados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                1. Dados que Coletamos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Dados de Cadastro:</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Nome, email, data de nascimento</li>
                  <li>Foto de perfil (opcional)</li>
                  <li>Peso, altura e informações físicas para cálculos de saúde</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Dados de Atividade:</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Histórico de corridas (distância, tempo, pace)</li>
                  <li>Calorias queimadas e métricas de desempenho</li>
                  <li>Conquistas e progresso de gamificação</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Geolocalização */}
          <Card className="border-orange-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" />
                2. Dados de Geolocalização (GPS)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Coletamos dados de localização GPS durante suas corridas para:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Rastrear sua rota em tempo real</li>
                <li>Calcular distância percorrida com precisão</li>
                <li>Exibir mapas das suas corridas</li>
                <li>Funcionalidade SOS de emergência</li>
                <li>Encontrar corredores e eventos próximos</li>
              </ul>
              <div className="bg-orange-500/10 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                  Importante: Você pode desativar o rastreamento de localização nas configurações 
                  do dispositivo, mas isso limitará funcionalidades essenciais do app.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Retenção de Dados GPS:</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Coordenadas precisas são armazenadas associadas às suas corridas</li>
                  <li>Você pode optar por não compartilhar rotas com outros usuários</li>
                  <li>Dados podem ser exportados ou excluídos a qualquer momento</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Dados de Saúde */}
          <Card className="border-red-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                3. Dados de Saúde (HealthKit / Google Fit)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Com sua permissão explícita, acessamos dados de saúde das plataformas nativas:
              </p>
              
              <div>
                <h4 className="font-semibold mb-2">Apple HealthKit (iOS):</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Frequência cardíaca durante corridas</li>
                  <li>Passos e distância registrados</li>
                  <li>Calorias ativas queimadas</li>
                  <li>Dados de treinos anteriores</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Google Fit (Android):</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Frequência cardíaca de smartwatches conectados</li>
                  <li>Histórico de atividades físicas</li>
                  <li>Métricas de saúde sincronizadas</li>
                </ul>
              </div>

              <div className="bg-red-500/10 p-4 rounded-lg">
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">Proteção de Dados de Saúde:</h4>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>Dados de saúde são criptografados em trânsito e em repouso</li>
                  <li>Nunca vendemos ou compartilhamos dados de saúde com terceiros</li>
                  <li>Acesso é estritamente para melhorar sua experiência de treino</li>
                  <li>Você pode revogar acesso a qualquer momento nas configurações</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Integração Spotify */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="w-5 h-5 text-green-500" />
                4. Integração com Spotify
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Ao conectar sua conta Spotify, coletamos e utilizamos:
              </p>
              
              <div>
                <h4 className="font-semibold mb-2">Dados Acessados:</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Suas playlists e músicas salvas</li>
                  <li>Música atualmente em reprodução</li>
                  <li>Histórico de reprodução recente</li>
                  <li>Preferências musicais para recomendações</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Como Usamos:</h4>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Sincronizar BPM da música com ritmo da corrida</li>
                  <li>Gerar playlists adaptativas baseadas na intensidade</li>
                  <li>Recomendar músicas por localização (praia, parque, urbano)</li>
                  <li>Controlar reprodução durante corridas</li>
                </ul>
              </div>

              <div className="bg-green-500/10 p-4 rounded-lg">
                <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">Sobre a Integração Spotify:</h4>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>Tokens de acesso são criptografados com AES-256-GCM</li>
                  <li>Não armazenamos sua senha do Spotify</li>
                  <li>Você pode desconectar a qualquer momento nas Configurações</li>
                  <li>Seguimos as diretrizes da Spotify Developer Policy</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Segurança */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                5. Segurança dos Dados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Implementamos medidas técnicas e organizacionais para proteger seus dados:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Criptografia AES-256-GCM para tokens OAuth</li>
                <li>HTTPS/TLS para todas as comunicações</li>
                <li>Row Level Security (RLS) no banco de dados</li>
                <li>Autenticação segura com proteção contra senhas vazadas</li>
                <li>Logs de acesso e monitoramento de segurança</li>
              </ul>
            </CardContent>
          </Card>

          {/* Compartilhamento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                6. Compartilhamento de Dados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Seus dados podem ser compartilhados nas seguintes situações:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Com outros usuários:</strong> Perfil público, corridas compartilhadas, rankings</li>
                <li><strong>Com mentores:</strong> Progresso e métricas se você participar de mentoria</li>
                <li><strong>Provedores de serviço:</strong> Infraestrutura cloud, Spotify (música)</li>
                <li><strong>Autoridades:</strong> Quando exigido por lei ou ordem judicial</li>
              </ul>
              <div className="bg-muted/50 p-4 rounded-lg mt-4">
                <p className="text-sm">
                  <strong>Nunca vendemos seus dados pessoais.</strong> Não compartilhamos informações 
                  identificáveis com anunciantes ou terceiros para fins de marketing.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Seus Direitos LGPD */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-primary" />
                7. Seus Direitos (LGPD)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Conforme a LGPD, você tem os seguintes direitos:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Acesso:</strong> Solicitar cópia de todos os seus dados</li>
                <li><strong>Correção:</strong> Atualizar informações incorretas</li>
                <li><strong>Exclusão:</strong> Solicitar remoção completa dos dados</li>
                <li><strong>Portabilidade:</strong> Exportar dados em formato estruturado</li>
                <li><strong>Revogação:</strong> Retirar consentimentos a qualquer momento</li>
                <li><strong>Oposição:</strong> Recusar processamento para fins específicos</li>
              </ul>
              <div className="bg-primary/10 p-4 rounded-lg mt-4">
                <p className="text-sm">
                  Para exercer seus direitos, acesse <strong>Configurações → Privacidade</strong> no app 
                  ou entre em contato: <a href="mailto:privacidade@youbetterrun.app" className="text-primary hover:underline">privacidade@youbetterrun.app</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Retenção */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">8. Retenção de Dados</h2>
            <p className="text-muted-foreground">
              Mantemos seus dados enquanto sua conta estiver ativa. Após exclusão da conta:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Dados pessoais são removidos em até 30 dias</li>
              <li>Backups são eliminados em até 90 dias</li>
              <li>Dados anonimizados podem ser retidos para análise estatística</li>
            </ul>
          </section>

          {/* Menores */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">9. Menores de Idade</h2>
            <p className="text-muted-foreground">
              O YBR não é destinado a menores de 13 anos. Se você é menor de 18 anos, 
              recomendamos obter consentimento dos responsáveis antes de usar o aplicativo. 
              Se tomarmos conhecimento de dados de menores de 13 anos, eles serão excluídos imediatamente.
            </p>
          </section>

          {/* Alterações */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">10. Alterações nesta Política</h2>
            <p className="text-muted-foreground">
              Podemos atualizar esta política periodicamente. Notificaremos sobre mudanças 
              significativas por email ou notificação no app. O uso continuado após alterações 
              constitui aceitação da nova política.
            </p>
          </section>

          {/* Contato */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">11. Contato</h2>
            <p className="text-muted-foreground">
              Para dúvidas sobre privacidade ou exercício de direitos:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Email: <a href="mailto:privacidade@youbetterrun.app" className="text-primary hover:underline">privacidade@youbetterrun.app</a></li>
              <li>Encarregado de Dados (DPO): <a href="mailto:dpo@youbetterrun.app" className="text-primary hover:underline">dpo@youbetterrun.app</a></li>
            </ul>
          </section>

          <div className="pt-6 border-t text-sm text-muted-foreground">
            <p>Última atualização: {new Date().toLocaleDateString('pt-BR')}</p>
            <p className="mt-2">You Better Run - Todos os direitos reservados</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
